import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { InsightResult, InsightStatus } from '../models/insight.types'
import { groupInsightsByOutcome, isNewResult } from '../service/insightPresentation'

export type InsightWorkspaceId = string | number

/** Workspace-scoped consumption cache. Admin/workbench data deliberately stays component-local. */
export const useInsightStore = defineStore('insights', () => {
  const list = ref<InsightResult[]>([])
  const count = ref(0)
  const detail = ref<Record<number, InsightResult>>({})
  const panelCache = ref<Record<string, InsightResult[]>>({})
  const panelCountCache = ref<Record<string, number>>({})
  const panelGeneration = ref(0)
  const panelOpen = ref(false)
  const selectedInsightId = ref<number | null>(null)
  const loadedWorkspaceId = ref<InsightWorkspaceId | null>(null)
  // Set only by a completed `setList` — distinct from `loadedWorkspaceId`, which `ensureWorkspace`
  // claims before the fetch resolves. Lets a consumer skip a redundant fetch when this workspace's
  // list is already in hand, without racing a fetch still in flight for the same workspace.
  const listFetchedAt = ref<number | null>(null)

  const byOutcome = computed(() => groupInsightsByOutcome(list.value))
  const newCount = computed(() => list.value.filter((item) => isNewResult(item)).length)

  function topN(limit: number): InsightResult[] {
    return list.value.slice(0, Math.max(0, limit))
  }

  function ensureWorkspace(workspaceId: InsightWorkspaceId): void {
    if (loadedWorkspaceId.value === workspaceId) return
    const pendingDeepLink = loadedWorkspaceId.value === null ? selectedInsightId.value : null
    resetState()
    loadedWorkspaceId.value = workspaceId
    selectedInsightId.value = pendingDeepLink
  }

  function ownsWorkspace(workspaceId: InsightWorkspaceId): boolean {
    return loadedWorkspaceId.value === workspaceId
  }

  function setList(
    workspaceId: InsightWorkspaceId,
    results: readonly InsightResult[],
    total: number,
  ): boolean {
    if (!ownsWorkspace(workspaceId)) return false
    list.value = [...results]
    count.value = total
    listFetchedAt.value = Date.now()
    return true
  }

  /** True only once `setList` has actually landed for this workspace — `ownsWorkspace` alone
   *  goes true the moment `ensureWorkspace` claims it, before the fetch resolves. */
  function hasFreshList(workspaceId: InsightWorkspaceId): boolean {
    return ownsWorkspace(workspaceId) && listFetchedAt.value !== null
  }

  function appendPage(
    workspaceId: InsightWorkspaceId,
    results: readonly InsightResult[],
    total: number,
  ): boolean {
    if (!ownsWorkspace(workspaceId)) return false
    const known = new Set(list.value.map((item) => item.id))
    list.value.push(...results.filter((item) => !known.has(item.id)))
    count.value = total
    return true
  }

  function setDetail(workspaceId: InsightWorkspaceId, result: InsightResult): boolean {
    if (!ownsWorkspace(workspaceId)) return false
    detail.value = { ...detail.value, [result.id]: result }
    return true
  }

  function updateStatus(
    workspaceId: InsightWorkspaceId,
    insightId: number,
    status: InsightStatus,
  ): boolean {
    if (!ownsWorkspace(workspaceId)) return false
    const cached = detail.value[insightId]
    if (cached) detail.value = { ...detail.value, [insightId]: { ...cached, status } }
    list.value = list.value.map((item) => (item.id === insightId ? { ...item, status } : item))
    return true
  }

  function setPanelInsights(
    workspaceId: InsightWorkspaceId,
    pageKey: string,
    results: readonly InsightResult[],
    total = results.length,
  ): boolean {
    if (!ownsWorkspace(workspaceId)) return false
    panelCache.value = { ...panelCache.value, [pageKey]: [...results] }
    panelCountCache.value = { ...panelCountCache.value, [pageKey]: total }
    return true
  }

  function invalidatePanelCache(): void {
    panelCache.value = {}
    panelCountCache.value = {}
    panelGeneration.value += 1
  }

  function openInsight(insightId: number): void {
    selectedInsightId.value = insightId
  }

  function closeInsight(): void {
    selectedInsightId.value = null
  }

  function togglePanel(): void {
    panelOpen.value = !panelOpen.value
  }

  function closePanel(): void {
    panelOpen.value = false
  }

  function resetState(): void {
    list.value = []
    count.value = 0
    detail.value = {}
    panelCache.value = {}
    panelCountCache.value = {}
    panelOpen.value = false
    selectedInsightId.value = null
    loadedWorkspaceId.value = null
    listFetchedAt.value = null
  }

  return {
    list,
    count,
    detail,
    panelCache,
    panelCountCache,
    panelGeneration,
    panelOpen,
    selectedInsightId,
    loadedWorkspaceId,
    listFetchedAt,
    byOutcome,
    newCount,
    topN,
    ensureWorkspace,
    ownsWorkspace,
    hasFreshList,
    setList,
    appendPage,
    setDetail,
    updateStatus,
    setPanelInsights,
    invalidatePanelCache,
    openInsight,
    closeInsight,
    togglePanel,
    closePanel,
    resetState,
  }
})

export type InsightStore = ReturnType<typeof useInsightStore>
